import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill, SITE_LABEL } from "@/components/Layout";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

function fmtTime(iso) {
  return iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "—";
}
function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }) : "—";
}

function NoteDialog({ shift, onSaved }) {
  const [note, setNote] = useState(shift.employee_note || "");
  const [open, setOpen] = useState(false);
  const save = async () => {
    try {
      await api.post(`/shifts/${shift.id}/note`, { note });
      toast.success("Note saved");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`shift-note-${shift.id}`}>
          {shift.employee_note ? "Edit note" : "Add note"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Shift note</DialogTitle>
        </DialogHeader>
        <Textarea
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Forgot to clock out — actually left at 5pm"
          data-testid="shift-note-textarea"
        />
        <DialogFooter>
          <Button onClick={save} className="bg-[#1A2744] text-white" data-testid="shift-note-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Timesheet() {
  const [range, setRange] = useState("week");
  const [shifts, setShifts] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/shifts/mine?range=${range}`);
    setShifts(data.shifts);
  }, [range]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const totalHours = shifts.reduce((a, s) => a + (s.total_minutes || 0), 0) / 60;

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      <header>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">My Timesheet</h1>
        <p className="text-gray-500 mt-1">Review your shifts, add notes where needed. Timestamps can only be edited by an admin.</p>
      </header>

      <Tabs value={range} onValueChange={setRange}>
        <TabsList>
          <TabsTrigger value="week" data-testid="timesheet-tab-week">This week</TabsTrigger>
          <TabsTrigger value="month" data-testid="timesheet-tab-month">This month</TabsTrigger>
        </TabsList>
        <TabsContent value={range} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
                <div className="text-sm text-gray-500">{shifts.length} shifts</div>
                <div className="text-sm">
                  <span className="text-gray-500">Total: </span>
                  <span className="font-heading text-lg font-bold text-[#1A2744]" data-testid="timesheet-total-hours">
                    {totalHours.toFixed(2)} hrs
                  </span>
                </div>
              </div>
              {/* Phones: one card per shift — no sideways scrolling. */}
              <div className="md:hidden divide-y divide-gray-100 border-t border-gray-100 px-4" data-testid="timesheet-cards">
                {shifts.length === 0 && (
                  <p className="py-10 text-center text-gray-500">No shifts yet.</p>
                )}
                {shifts.map((s) => (
                  <div key={s.id} className="py-3 space-y-1.5" data-testid={`timesheet-card-${s.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-[#1A2744]">{fmtDate(s.clock_in_ts)}</p>
                        <p className="text-sm text-gray-600">
                          {fmtTime(s.clock_in_ts)} → {fmtTime(s.clock_out_ts)}
                          <span className="text-gray-400"> · {SITE_LABEL[s.site]}</span>
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-heading text-lg font-bold text-[#1A2744]">
                          {s.total_minutes ? (s.total_minutes / 60).toFixed(2) : "—"}
                          <span className="text-xs font-normal text-gray-500"> hrs</span>
                        </p>
                        {s.break_minutes > 0 && (
                          <span className="text-[10px] text-amber-700">-{s.break_minutes}m break</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <StatusPill status={s.status} />
                      <NoteDialog shift={s} onSaved={load} />
                    </div>
                    {s.employee_note && (
                      <p className="text-xs text-gray-500 line-clamp-2">{s.employee_note}</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Clock in</TableHead>
                      <TableHead>Clock out</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Site</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shifts.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-10">No shifts yet.</TableCell></TableRow>
                    )}
                    {shifts.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{fmtDate(s.clock_in_ts)}</TableCell>
                        <TableCell>{fmtTime(s.clock_in_ts)}</TableCell>
                        <TableCell>{fmtTime(s.clock_out_ts)}</TableCell>
                        <TableCell>
                          {s.total_minutes ? (s.total_minutes / 60).toFixed(2) : "—"}
                          {s.break_minutes > 0 && (
                            <span
                              className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                              title={`Auto-deducted ${s.break_minutes} min break`}
                            >
                              -{s.break_minutes}m break
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">{SITE_LABEL[s.site]}</TableCell>
                        <TableCell><StatusPill status={s.status} /></TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs text-gray-500">{s.employee_note || "—"}</TableCell>
                        <TableCell><NoteDialog shift={s} onSaved={load} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
